import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Hook for managing pan/drag behavior in the Gantt chart
 * Supports both horizontal and vertical panning
 * 
 * ONLY handles LEFT mouse button - right-click is for selection box
 */
export const useGanttPan = (chartRef) => {
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const hasPannedRef = useRef(false);

  const handleMouseDown = useCallback((e) => {
    // ONLY left mouse button (button 0)
    if (e.button !== 0) return;
    
    // Don't pan if clicking on a bar
    if (e.target.closest('.gantt-bar')) return;
    
    // Don't pan if clicking on sidebar
    if (e.target.closest('.gantt-row__sidebar')) return;
    if (e.target.closest('.gantt-timeline-header__sidebar')) return;
    if (e.target.closest('.gantt-swimlane__header-sidebar')) return;
    
    const timelineEl = e.target.closest('.gantt-row__timeline, .gantt-timeline-header__timeline, .gantt-timeline-header__columns, .gantt-timeline-header__groups, .gantt-swimlane__header-timeline');
    if (!timelineEl && !e.target.closest('.gantt-view__task-list')) return;
    
    setIsPanning(true);
    hasPannedRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: chartRef.current?.scrollLeft || 0,
      scrollTop: chartRef.current?.scrollTop || 0,
    };
    
    e.preventDefault();
  }, [chartRef]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning || !chartRef.current) return;
    
    const deltaX = e.clientX - panStartRef.current.x;
    const deltaY = e.clientY - panStartRef.current.y;
    
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      hasPannedRef.current = true;
    }
    
    chartRef.current.scrollLeft = panStartRef.current.scrollLeft - deltaX;
    chartRef.current.scrollTop = panStartRef.current.scrollTop - deltaY;
  }, [isPanning, chartRef]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      // Reset hasPanned after a short delay so click handlers can check it
      setTimeout(() => {
        hasPannedRef.current = false;
      }, 50);
    }
  }, [isPanning]);

  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp]);

  return {
    isPanning,
    hasPannedRef,
    handleMouseDown,
  };
};

export default useGanttPan;
